import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Settings, Home, Plus, Trash2, Edit, ShieldCheck, Eye, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role configuration
const ROLES = {
  viewer: { label: "Viewer", icon: Eye, color: "bg-slate-500", description: "Can view protocols only" },
  editor: { label: "Editor", icon: Edit, color: "bg-blue-500", description: "Can edit protocols, medications, procedures" },
  admin: { label: "Admin", icon: ShieldCheck, color: "bg-primary", description: "Full access including user management" },
  demo_admin: { label: "Sandbox Admin", icon: ShieldCheck, color: "bg-amber-500", description: "Practice mode — sees every admin screen but writes are not persisted" },
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [creating, setCreating] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, userId: null, email: "" });
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate("/login");
      }
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await axios.post(`${API}/admin/users/create`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${ROLES[newUser.role].label} account created for ${newUser.email}`);
      setNewUser({ email: "", password: "", name: "", role: "viewer" });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Role updated to ${ROLES[newRole].label}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete viewer account for ${email}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openResetPasswordDialog = (userId, email) => {
    setResetPasswordDialog({ open: true, userId, email });
    setNewPassword("");
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setResetting(true);
    try {
      await axios.put(`${API}/admin/users/${resetPasswordDialog.userId}/reset-password`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetPasswordDialog.email}`);
      setResetPasswordDialog({ open: false, userId: null, email: "" });
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-heading font-bold flex items-center gap-2">
            <Users className="w-5 h-5" />
            User Management
          </h1>
          <div className="flex items-center gap-2">
            <Link to="/admin/dashboard"><Button variant="outline" size="sm"><Settings className="w-4 h-4 mr-2" />Dashboard</Button></Link>
            <Link to="/"><Button variant="outline" size="sm"><Home className="w-4 h-4 mr-2" />App</Button></Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Create User Form */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create User Account
              </CardTitle>
              <Button 
                variant={showCreateForm ? "outline" : "default"}
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                {showCreateForm ? "Cancel" : "Add New User"}
              </Button>
            </div>
          </CardHeader>
          {showCreateForm && (
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-email">Email</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-name">Name</Label>
                    <Input
                      id="new-name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      placeholder="Create a password"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-role">Role</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            <span>Viewer</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="editor">
                          <div className="flex items-center gap-2">
                            <Edit className="w-4 h-4" />
                            <span>Editor</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" />
                            <span>Admin</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="demo_admin">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-amber-400" />
                            <span>Sandbox Admin (practice mode)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{ROLES[newUser.role].description}</p>
                  </div>
                </div>
                <Button type="submit" disabled={creating}>
                  {creating ? "Creating..." : `Create ${ROLES[newUser.role].label} Account`}
                </Button>
              </form>
            </CardContent>
          )}
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>All Users ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No users found. Create your first user account above.</p>
            ) : (
              <div className="space-y-2">
                {users.map((user) => {
                  const roleConfig = ROLES[user.role] || ROLES.viewer;
                  const RoleIcon = roleConfig.icon;
                  return (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${roleConfig.color}`}>
                          <RoleIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium">{user.email}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                            <Badge variant={user.role === 'admin' ? 'default' : user.role === 'editor' ? 'outline' : 'secondary'} 
                                   className={user.role === 'editor' ? 'border-blue-500 text-blue-500' : ''}>
                              {roleConfig.label}
                            </Badge>
                            {user.name && <span>{user.name}</span>}
                            <span>•</span>
                            <span className={user.is_active ? 'text-green-500' : 'text-red-500'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {user.role !== 'admin' && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Select value={user.role} onValueChange={(value) => handleChangeRole(user.id, value)}>
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="demo_admin">Sandbox Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openResetPasswordDialog(user.id, user.email)}
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleToggleActive(user.id)}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDeleteUser(user.id, user.email)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => !open && setResetPasswordDialog({ open: false, userId: null, email: "" })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Reset Password
              </DialogTitle>
              <DialogDescription>
                Set a new password for <strong>{resetPasswordDialog.email}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, userId: null, email: "" })}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword} disabled={resetting || newPassword.length < 6}>
                {resetting ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsers;
